import {
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';

import {useAuth} from '../../../../hooks/useAuth';
import {useVerifiedPermissions} from '../../../../hooks/useVerifiedPermissions';

import {
    add,
    deleteRecord,
    fetch,
    fetchRecords,
    update,
} from '../services/AccountsService';

import {Account} from '../types';

// FNP-945: records are React Query's job; selection stays in Redux (FNP-951).
//
// This file existed before the migration as an abandoned attempt with zero
// importers. It was RESUMED rather than deleted, because it carried something
// the Redux path did not: the `accounts:manage:all` RBAC gate below. Only
// system admins may list all accounts per the GraphQL schema, and the Redux
// thunk dispatched the list fetch unconditionally.
//
// Its old `useAccount` export was REMOVED — it name-collided with the live
// Redux hook in the sibling file, which was a readable trap. The single-record
// hook is `useAccountQuery` here, consumed by useAccount.ts.

export const accountsKeys = {
    all: ['accounts'] as const,
    lists: () => [...accountsKeys.all, 'list'] as const,
    list: (filter?: object) => [...accountsKeys.lists(), filter ?? {}] as const,
    details: () => [...accountsKeys.all, 'detail'] as const,
    detail: (id: string) => [...accountsKeys.details(), id] as const,
};

export const useAccountsQuery = (filter?: { [key: string]: string }) => {
    const {allowedActions} = useVerifiedPermissions();

    // Only system admins can list all accounts (per GraphQL schema).
    const canListAccounts = allowedActions.includes('accounts:manage:all');

    return useQuery({
        queryKey: accountsKeys.list(filter),
        queryFn: async (): Promise<Account[]> => {
            // listAccounts returns an AccountConnection ({items, nextToken}),
            // not a bare array — unwrap it here so consumers keep getting a
            // list. nextToken is not paged through yet: the portal's accounts
            // list has always shown the first page only, and this migration
            // deliberately does not change that behaviour.
            const connection = await fetchRecords(filter);
            return connection?.items ?? [];
        },
        enabled: canListAccounts,
    });
};

// The signed-in user's own account.
export const useCurrentAccount = () => {
    const {user} = useAuth();

    return useQuery({
        queryKey: accountsKeys.detail(user?.userId ?? ''),
        queryFn: () => fetch(user?.userId ?? ''),
        enabled: !!user?.userId,
    });
};

export const useAccountQuery = (accountId?: string) => {
    return useQuery({
        queryKey: accountsKeys.detail(accountId ?? ''),
        queryFn: () => fetch(accountId as string),
        enabled: !!accountId,
    });
};

export const useCreateAccountMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (values: Account) => add(values),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: accountsKeys.lists()});
        },
    });
};

export const useUpdateAccountMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
                         id,
                         record
                     }: { id: string, record: Account }) => update(id, record),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({queryKey: accountsKeys.lists()});
            queryClient.invalidateQueries({queryKey: accountsKeys.detail(variables.id)});
        },
    });
};

export const useDeleteAccountMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteRecord(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: accountsKeys.lists()});
        },
    });
};
